/* eslint-disable no-unused-expressions, no-magic-numbers, class-methods-use-this, no-empty-function, no-nested-ternary, no-extra-parens, max-lines */
import ResizeTracker from 'utils/ResizeTracker';
import scrollTo from 'ui/scrollTo';

class Tabs {

    /**
     * Make a new Tabs interface.
     * @constructs
     * @param {HTMLElement} element
     * @param {{}} options
     */
    constructor(element, options) {
        this._element = element;

        this._options = {
            ...options,
            mobileSize: 768,
            timeoutHover: 200,
            timeoutOut: 300
        };

        this._state = {
            oldTab: null,
            newTab: null,
            isMobile: false,
            init: false,
            index: 0,
            isIntersecting: false,
            observer: null,
            tabIds: [],
            noMobileTabs: true
        };

        this._rootContainer = document.body;

        this._selectors = {
            tabs: this._options.tabs ? this._options.tabs : '.financial-overview__tab-link',
            mobiletabs: this._options.mobiletabs ? this._options.mobiletabs : '.financial-overview__mobiletab-link',
            panels: this._options.panels ? this._options.panels : '[role="tabpanel"]',
            tablist: this._options.tablist ? this._options.tablist : '.financial-overview__tabs-list',
            mobiletablist: this._options.mobiletablist ? this._options.mobiletablist : '.financial-overview__content-wrapper'
        };

        this._setIsMobile = this._setIsMobile.bind(this);
        this._removeTabHandler = this._removeTabHandler.bind(this);
        this._setTabHandlers = this._setTabHandlers.bind(this);
        this._setupObserver = this._setupObserver.bind(this);

        if (typeof window.CustomEvent === 'function') {
            this.customEvent = new CustomEvent('initchart', {
                detail: {},
                bubbles: true,
                cancelable: true,
                composed: false
            });
        } else {
            // Custom Event for ie11
            this.customEvent = document.createEvent('CustomEvent');
            this.customEvent.initCustomEvent('initchart', true, false, null);
        }

        this._cacheSelectors();
        this._initialize();
    }

    /**
     * Cache elements
     * @private
     */
    _cacheSelectors() {
        this._elements = {
            tabs: [...this._element.querySelectorAll(`${this._selectors.tabs}`)],
            mobiletabs: this._options.mobiletablist !== 'none' && [...this._element.querySelectorAll(this._selectors.mobiletabs)],
            panels: [...this._element.querySelectorAll(this._selectors.panels)],
            tablist: this._element.querySelector(this._selectors.tablist),
            mobiletablist: this._options.mobiletablist !== 'none' && this._element.querySelector(this._selectors.mobiletablist),
            container: [...this._element.querySelectorAll('.financial-overview__content')],
            header: document.querySelector('.header')
        };

    }

    /**
     * Initialize
     * @private
     */
    _initialize() {
        this._element.setAttribute('data-current', this._state.index);
        this._addListeners();
    }

    /**
     * Initialize
     * @private
     */
    _switchTab() {
        const hasMobileTabs = this._elements.mobiletabs.length > 0;
        const ariaAtribute = this._state.isMobile && hasMobileTabs ? 'aria-expanded' : 'aria-selected';
        this._state.newTab && this._state.newTab.focus();
        // Make the active tab focusable by the user (Tab key)
        // Set the selected state
        this._state.newTab.setAttribute(ariaAtribute, true);
        this._state.newTab.setAttribute('tabindex', '0');
        const hasTimeline = this._state.newTab.getAttribute('data-type') === 'timeline';
        // eslint-disable-next-line no-undefined
        const checkForTimelines = this._options.containsTimelines === undefined || this._options.containsTimelines === 'true' || this._options.containsTimelines;
        checkForTimelines && this._element.setAttribute('data-timeline', hasTimeline);
        this._state.oldTab && this._state.oldTab.removeAttribute(ariaAtribute);
        this._state.oldTab && this._state.oldTab.setAttribute('tabindex', '-1');
        const mobileTabs = hasMobileTabs ? this._elements.mobiletabs : this._elements.tabs;
        const tabs = this._state.isMobile && this._options.mobiletablist !== 'none' ? mobileTabs : this._elements.tabs;
        // Get the indices of the new and old tabs to find the correct tab panels to show and hide
        const index = tabs.indexOf(this._state.newTab);
        const oldIndex = tabs.indexOf(this._state.oldTab);
        this._elements.panels[oldIndex] && this._elements.panels[oldIndex].setAttribute('aria-hidden', true);
        this._elements.panels[index] && this._elements.panels[index].removeAttribute('aria-hidden');
        this._element.setAttribute('data-current', index);
        const timeout = setTimeout(() => {
            window.dispatchEvent(this.customEvent);
            clearTimeout(timeout);
        }, 600);
    }

    /**
     * Set mobile state
     * @private
     */
    _setIsMobile() {
        const isMobile = this._rootContainer.clientWidth < this._options.mobileSize && this._elements.mobiletabs.length > 0;
        isMobile && this._state.observer && this._removeObserver();
        !isMobile && this._setupObserver();
        const em = parseFloat(getComputedStyle(this._elements.header).fontSize) * 4;
        this._state.headerHeight = this._elements.header.offsetHeight + em;

        if (isMobile !== this._state.isMobile || !this._state.init) {
            this._state.isMobile = isMobile;
            this._setTabHandlers();
            this._handleFirstTab();
        }
    }

    /**
     * Store tab IDs in the sate
     * @private
     */
    _setTabIds() {
        this._state.tabIds = [];

        this._elements.panels.forEach(tab => {
            const id = tab.getAttribute('id');
            this._state.tabIds.push(id);
        });
    }

    /**
     * Add event listeners
     * @private
     */
    _addListeners() {

        this._resizeTracker = new ResizeTracker();

        this._resizeTracker.init(
            { element: window },
            this._setIsMobile
        );

        this._setIsMobile();
        this._setTabIds();
        this._handleFirstTab();


    }

    /**
     * Set intersection observer on sections
     *
     * @private
     */
    _setupObserver() {
        const options = {
            root: this._rootContainer,
            rootMargin: '0px',
            threshold: 0.5
        };

        this._state.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                // eslint-disable-next-line no-magic-numbers
                const isIntersecting = entry.isIntersecting && entry.intersectionRatio > 0.01;
                isIntersecting && isIntersecting !== this._state.isIntersecting && this._triggerIntersection();
                this._state.isIntersecting = isIntersecting;
            });
        }, options);

        this._state.observer.POLL_INTERVAL = 100;

        this._elements.container.forEach(element => {
            this._state.observer.observe(element);
        });

    }

    /**
     * Remove intersection observer
     *
     * @private
     */
    _removeObserver() {
        this._state.observer && this._state.observer.unobserve(this._element);


    }

    /**
     * Trigger the button animation
     *
     * @private
     */
    _triggerIntersection() {
        const currentPanel = this._elements.panels.find(panel => !panel.hasAttribute('hidden'));
        const timelineButtons = currentPanel.querySelectorAll('[data-is-open="false"] .timeline__header, .financial-overview__timeline:not([data-collapsible]) .timeline__provider-header');

        timelineButtons.forEach((button, index) => {
            const hoverTimeout = setTimeout(() => {
                button.classList.add('move');

                const outTimeout = setTimeout(() => {
                    button.classList.remove('move');
                    clearTimeout(outTimeout);
                }, this._options.timeoutOut);

                clearTimeout(hoverTimeout);
                // eslint-disable-next-line no-magic-numbers
            }, this._options.timeoutHover * (index + 1));
        });

    }

    /**
     * Add event listeners
     * @private
     */
    _handleFirstTab() {
        const hash = window.location.hash && window.location.hash.substr(1);
        const isPanel = hash && this._state.tabIds.includes(hash);

        if (isPanel) {
            const trigger = this._state.isMobile && this._elements.mobiletabs ? this._elements.mobiletabs.find(tab => tab.href === `#${hash}`) : this._elements.tabs.find(tab => tab.href === `#${hash}`);
            trigger && trigger.click();
            const currentTab = this._state.isMobile && this._elements.mobiletabs ? this._elements.mobiletablist.querySelector(`[aria-controls='${hash}']`) : this._elements.tablist.querySelector(`[href='#${hash}']`);
            this._state.oldTab = this._state.isMobile && this._elements.mobiletabs ? this._elements.mobiletablist[0] : this._elements.tablist[0];
            this._state.newTab = currentTab;
            this._elements.panels[0].setAttribute('aria-hidden', true);
            this._switchTab();
            this._state.index = this._state.tabIds.indexOf(hash);
            this._state.isMobile && this._elements.mobiletabs ? this._elements.mobiletabs[0].setAttribute('aria-expanded', false) : this._elements.tabs[0].setAttribute('aria-selected', false);

            const timeout = setTimeout(() => {
                const currentScroll = window.scrollY || window.scrollTop || window.pageYOffset;
                const target = this._element.querySelector(`#${hash}`);
                const targetBoundingBox = target.getBoundingClientRect();
                const taboffset = this._state.isMobile ? 0 : this._elements.tablist.offsetHeight * 2;
                const offset = targetBoundingBox.top + currentScroll - this._state.headerHeight - taboffset;
                scrollTo(null, offset);
                clearTimeout(timeout);
            }, 600);

            return;
        }


        this._state.isMobile && this._elements.mobiletabs && this._elements.tabs[0].setAttribute('aria-selected', false);

        !this._state.isMobile && this._elements.mobiletabs && this._elements.mobiletabs[0].setAttribute('aria-expanded', false);
    }

    /**
     * Set tabs handlers
     * @private
     */
    _setTabHandlers() {
        const hasMobileTabs = this._elements.mobiletabs.length > 0;
        const prefix = this._state.isMobile ? 'mobile-' : '';
        this._elements.panels.forEach(panel => {
            const suffix = panel.getAttribute('data-labelledby');
            panel.setAttribute('aria-labelledby', `${prefix}${suffix}`);
        });
        this._state.isMobile && this._elements.mobiletablist.setAttribute('role', 'tablist');
        !this._state.isMobile && hasMobileTabs && this._elements.mobiletablist.removeAttribute('role');
        this._state.oldTab = this._state.isMobile && this._elements.mobiletablist.length > 0 ? this._elements.mobiletablist.querySelector('[aria-expanded="true"]') : this._elements.tablist && this._elements.tablist.querySelector('[aria-selected="true"]');
        const tabs = this._state.isMobile ? this._elements.mobiletabs : this._elements.tabs;
        const tablist = this._state.isMobile ? this._elements.mobiletablist : this._elements.tablist;
        this._initTabs(tabs, tablist);
    }

    _initTabs(tabs, tablist) {
        this._state.isMobile && this._elements.mobiletabs && this._elements.mobiletabs.forEach((tab, index) => this._tabHandler(tab, index, tabs, tablist));
        !this._state.isMobile && this._elements.tabs.forEach((tab, index) => this._tabHandler(tab, index, tabs, tablist));
        this._state.init && !this._state.isMobile && this._elements.mobiletabs.length > 0 && this._elements.mobiletabs.forEach(tab => this._removeTabHandler(tab, tablist));
        this._state.init && this._state.isMobile && this._elements.tabs.forEach(tab => this._removeTabHandler(tab, tablist));
        this._state.init = true;
    }

    _tabHandler(tab, index, tabs, tablist) {
        const ariaAtribute = this._state.isMobile ? 'aria-expanded' : 'aria-selected';
        tab.setAttribute('role', 'tab');

        // Handle clicking of tabs for mouse users
        tab.addEventListener('click', event => {
            event.preventDefault();
            const currentTab = tablist.querySelector(`[${ariaAtribute}='true']`);
            this._state.oldTab = currentTab;
            this._state.newTab = event.currentTarget;
            if (event.currentTarget !== currentTab) {
                this._switchTab();
                this._state.index = index;
            }
        });

        // Handle keydown events for keyboard users
        tab.addEventListener('keydown', event => {
            // Get the index of the current tab in the tabs node list
            const id = tabs.indexOf(event.currentTarget);
            // Work out which key the user is pressing and
            // Calculate the new tab's index where appropriate
            const dir = event.which === 37 ? id - 1 : event.which === 39 ? id + 1 : event.which === 40 ? 'down' : null;
            if (dir !== null) {
                event.preventDefault();
                // If the down key is pressed, move focus to the open panel, otherwise switch to the adjacent tab
                this._state.oldTab = tabs[dir] && event.currentTarget;
                this._state.newTab = tabs[dir] && tabs[dir];
                this._state.index = index;
                const isDown = dir === 'down';
                isDown && this._elements.panels[index].focus();
                !isDown && tabs[dir] && this._switchTab();
            }
        });

    }

    _removeTabHandler(tab, tablist) {
        tab.removeAttribute('role');
        // eslint-disable-next-line no-magic-numbers
        tab.removeAttribute('id');
        tab.parentNode.setAttribute('role', '');
        tablist.setAttribute('role', '');
        // Remove clicking of tabs for mouse users
        tab.removeEventListener('click', () => { });

        // Handle keydown events for keyboard users
        tab.removeEventListener('keydown', () => { });
        this._elements.tabs.forEach((element, index) => {
            element.setAttribute('aria-selected', this._state.index === index);
            element.setAttribute('tabindex', this._state.index === index ? '0' : '-1');
        });
        this._elements.mobiletabs.length > 0 && this._elements.mobiletabs.forEach((element, index) => {
            element.setAttribute('aria-expanded', this._state.index === index);
        });

        this._state.init = false;
    }
}

export default Tabs;
/* eslint-enable */
